"use client"
import {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from "react";
import {GeoLocation} from "../../interfaces/Bar";


export function calculateDistance(p1: GeoLocation, p2: GeoLocation): number {
    return getDistance(p1.lat, p1.lng, p2.lat, p2.lng)
}

export function getDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // distance in km
}

function useRequestLocation(): LocationContextType {
    const watchIdRef = useRef<number | null>(null);
    const [location, setLocation] = useState<GeoLocation | null | undefined>(undefined);

    const [watching, setWatching] = useState<boolean|undefined>(undefined);
    useEffect(() => {
        if (watching) {
            if (watchIdRef.current !== null) return;
            if (!navigator.geolocation) {
                setLocation(null)
                console.log('Geolocation is not supported by your browser');
                setWatching(false);
                return;
            }
            let lastLocation: GeoLocation|undefined = undefined

            watchIdRef.current = navigator.geolocation.watchPosition(
                (position) => {
                    console.log("Got location ", position)
                    const l = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    }
                    if (lastLocation == undefined || getDistance(lastLocation?.lat, lastLocation?.lng, l.lat, l.lng) > 0.1) {
                        setLocation(l)
                        lastLocation = l
                    }
                },
                (err) => {
                    setLocation(null)
                    console.log(err.message);
                    setWatching(false)
                }
            );
        }
    }, [watching, setLocation]);

    useEffect(() => {
        const checkPermissions = async () => {
            const granted = (await navigator.permissions.query({ name: "geolocation" })).state != "denied"
            setWatching(granted)
        }
        checkPermissions()
    }, []);

    const startWatching = useCallback(() => {
        setWatching(true)
    }, [])

    const stopWatching = useCallback(() => {
        setWatching(false)
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    }, [])
    return {location: location, startWatching, stopWatching, watching}
}

export interface LocationContextType {
    location: GeoLocation|undefined|null;
    startWatching: () => void;
    stopWatching: () => void;
    watching: boolean|undefined; // undefined before we get a chance to check permission grant.
}

const LocationContext = createContext<LocationContextType|null>(null);

export function LocationProvider({children}: {children: ReactNode}): ReactNode {
    const locationData = useRequestLocation();
    return (
        <LocationContext.Provider value={locationData}>
            {children}
        </LocationContext.Provider>
    )
}

export const useGeolocation = () => {
    const ctx = useContext(LocationContext);
    if (ctx === null) throw new Error("useSharedLocation must be used within LocationProvider");
    return ctx;
};